import _slicedToArray from "/home/javi/repos/infomerc/tasa-collectum/ecommerce/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import React, { useRef, useCallback, useMemo, useEffect } from "react";
import styled from "styled-components";
import Portal from "../Portal";
import PopoverContentWrapper from "./components/ContentWrapper";
import useTheme from "../hooks/useTheme";
import useStateWithTimeout from "../hooks/useStateWithTimeout";
import { POSITIONS, ALIGNS } from "./consts";
import Separator from "../Separator";
import defaultTheme from "../defaultTheme";
var StyledPopoverChild = styled.div.withConfig({
  displayName: "Popover__StyledPopoverChild",
  componentId: "t88fx4-0"
})(["position:relative;"]);
var StyledActions = styled.div.withConfig({
  displayName: "Popover__StyledActions",
  componentId: "t88fx4-1"
})(["margin-top:", ";"], function (_ref) {
  var theme = _ref.theme;
  return theme.orbit.spaceSmall;
});
StyledActions.defaultProps = {
  theme: defaultTheme
};

var Popover = function Popover(_ref2) {
  var children = _ref2.children,
      content = _ref2.content,
      _ref2$preferredPositi = _ref2.preferredPosition,
      preferredPosition = _ref2$preferredPositi === void 0 ? POSITIONS.BOTTOM : _ref2$preferredPositi,
      _ref2$preferredAlign = _ref2.preferredAlign,
      preferredAlign = _ref2$preferredAlign === void 0 ? ALIGNS.START : _ref2$preferredAlign,
      dataTest = _ref2.dataTest,
      opened = _ref2.opened,
      width = _ref2.width,
      noPadding = _ref2.noPadding,
      overlapped = _ref2.overlapped,
      onClose = _ref2.onClose,
      onOpen = _ref2.onOpen,
      fixed = _ref2.fixed,
      actions = _ref2.actions;
  var theme = useTheme();
  var transitionLength = useMemo(function () {
    return parseFloat(theme.orbit.durationFast) * 1000;
  }, [theme.orbit.durationFast]);

  var _useStateWithTimeout = useStateWithTimeout(false, transitionLength),
      _useStateWithTimeout2 = _slicedToArray(_useStateWithTimeout, 4),
      shown = _useStateWithTimeout2[0],
      setShown = _useStateWithTimeout2[1],
      setShownWithTimeout = _useStateWithTimeout2[2],
      clearShownTimeout = _useStateWithTimeout2[3];

  var _useStateWithTimeout3 = useStateWithTimeout(false, transitionLength),
      _useStateWithTimeout4 = _slicedToArray(_useStateWithTimeout3, 4),
      render = _useStateWithTimeout4[0],
      setRender = _useStateWithTimeout4[1],
      setRenderWithTimeout = _useStateWithTimeout4[2],
      clearRenderTimeout = _useStateWithTimeout4[3];

  var container = useRef(null);
  var resolveCallback = useCallback(function (state) {
    if (onClose && !state) onClose();
    if (onOpen && state) onOpen();
  }, [onClose, onOpen]);
  var handleOut = useCallback(function (ev) {
    // If open prop is present ignore custom handler
    if (container.current && !container.current.contains(ev.target)) {
      if (typeof opened === "undefined") {
        setShown(false);
        clearShownTimeout();
        setRenderWithTimeout(false);
        resolveCallback(false);
      } else if (onClose) onClose();
    }
  }, [clearShownTimeout, onClose, opened, resolveCallback, setRenderWithTimeout, setShown]);
  var handleClick = useCallback(function () {
    // If open prop is present ignore custom handler
    if (typeof opened === "undefined") {
      if (shown) {
        setShown(false);
        clearShownTimeout();
        setRenderWithTimeout(false);
        resolveCallback(false);
      } else {
        setRender(true);
        clearRenderTimeout();
        setShownWithTimeout(true);
        resolveCallback(true);
      }
    } else if (opened) {
      resolveCallback(false);
    } else if (!opened) {
      resolveCallback(true);
    }
  }, [clearRenderTimeout, clearShownTimeout, opened, resolveCallback, setRender, setRenderWithTimeout, setShown, setShownWithTimeout, shown]);
  useEffect(function () {
    if (typeof opened !== "undefined") {
      if (opened) {
        setRender(true);
        clearRenderTimeout();
        setShownWithTimeout(true);
      } else {
        setShown(false);
        clearShownTimeout();
        setRenderWithTimeout(false);
      }
    }
  }, [opened, clearRenderTimeout, clearShownTimeout, setRender, setShown, setShownWithTimeout, setRenderWithTimeout]);
  return React.createElement(React.Fragment, null, React.createElement(StyledPopoverChild, {
    onClick: handleClick,
    ref: container
  }, children), render && React.createElement(Portal, {
    renderInto: "popovers"
  }, React.createElement(PopoverContentWrapper, {
    shown: shown,
    width: width,
    containerRef: container,
    preferredPosition: preferredPosition,
    preferredAlign: preferredAlign,
    onClose: handleOut,
    dataTest: dataTest,
    noPadding: noPadding,
    overlapped: overlapped,
    fixed: fixed,
    actions: actions
  }, content, actions && React.createElement(StyledActions, null, React.createElement(Separator, {
    spaceAfter: "normal"
  }), " ", actions))));
};

export default Popover;